import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";

const SITE = "https://hireds.in";

// Slug → exact DB-stored state name. Add states as DB grows.
export const STATES = {
  "uttar-pradesh-government-jobs": { state: "Uttar Pradesh", short: "UP" },
  "bihar-government-jobs": { state: "Bihar", short: "Bihar" },
  "maharashtra-government-jobs": { state: "Maharashtra", short: "Maharashtra" },
  "madhya-pradesh-government-jobs": { state: "Madhya Pradesh", short: "MP" },
  "rajasthan-government-jobs": { state: "Rajasthan", short: "Rajasthan" },
  "delhi-government-jobs": { state: "Delhi", short: "Delhi" },
  "karnataka-government-jobs": { state: "Karnataka", short: "Karnataka" },
  "tamil-nadu-government-jobs": { state: "Tamil Nadu", short: "TN" },
  "west-bengal-government-jobs": { state: "West Bengal", short: "WB" },
  "gujarat-government-jobs": { state: "Gujarat", short: "Gujarat" },
  "andhra-pradesh-government-jobs": { state: "Andhra Pradesh", short: "AP" },
  "telangana-government-jobs": { state: "Telangana", short: "Telangana" },
  "kerala-government-jobs": { state: "Kerala", short: "Kerala" },
  "punjab-government-jobs": { state: "Punjab", short: "Punjab" },
  "haryana-government-jobs": { state: "Haryana", short: "Haryana" },
  "odisha-government-jobs": { state: "Odisha", short: "Odisha" },
};

export default function StateJobsPage() {
  const { stateSlug } = useParams();
  const meta = STATES[stateSlug];
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!meta) { setLoading(false); return; }
    setLoading(true);
    api.get(`/jobs?state=${encodeURIComponent(meta.state)}&limit=50`)
      .then((r) => { setJobs(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [stateSlug, meta]);

  // Per-page SEO
  useEffect(() => {
    if (!meta) return;
    const oldTitle = document.title;
    document.title = `${meta.state} Government Jobs 2026 — Latest ${meta.short} Sarkari Naukri | hireds.in`;
    const md = document.querySelector('meta[name="description"]');
    const oldMd = md ? md.getAttribute("content") : null;
    if (md) md.setAttribute("content", `Latest ${meta.state} government job notifications 2026 — state PSC, police, teacher, board, university recruitment. Daily updated with eligibility, last date and official apply links.`);
    const canon = document.querySelector('link[rel="canonical"]');
    const oldCanon = canon ? canon.getAttribute("href") : null;
    if (canon) canon.setAttribute("href", `${SITE}/states/${stateSlug}`);
    return () => {
      document.title = oldTitle;
      if (md && oldMd != null) md.setAttribute("content", oldMd);
      if (canon && oldCanon != null) canon.setAttribute("href", oldCanon);
    };
  }, [meta, stateSlug]);

  if (!meta) {
    return <Navigate to="/states" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
      <Breadcrumbs items={[
        { name: "Home", url: "/" },
        { name: "State Govt Jobs", url: "/states/uttar-pradesh-government-jobs" },
        { name: meta.state, url: `/states/${stateSlug}` },
      ]} />

      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">State Recruitment</p>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          {meta.state} Government Jobs 2026
        </h1>
        <p className="text-base text-slate-700 mt-2 leading-relaxed">
          All live vacancy notifications for {meta.state} state government, public service commission, police, teacher recruitment and board exams. {total} live notifications, refreshed daily at 06:00 IST.
        </p>
      </header>

      <section data-testid="state-jobs-list">
        {loading && <div className="text-slate-500">Loading…</div>}
        {!loading && !jobs.length && (
          <div className="bg-white border border-slate-200 rounded-lg p-8">
            <h2 className="text-xl font-bold text-slate-900 mb-2">No live {meta.state} notifications today</h2>
            <p className="text-sm text-slate-700 mb-4">New state vacancies are added every weekday. Subscribe via WhatsApp / Telegram for instant alerts.</p>
            <div className="flex gap-3 flex-wrap">
              <Link to="/all-india-jobs" className="inline-flex items-center min-h-11 px-4 py-2 rounded-md bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold">All India Jobs</Link>
              <Link to="/sectors/state-govt" className="inline-flex items-center min-h-11 px-4 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm font-semibold">State Govt Sector</Link>
            </div>
          </div>
        )}
        {!loading && jobs.length > 0 && (
          <ul className="space-y-3">
            {jobs.map((j) => (
              <li key={j.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:border-orange-200 transition-colors">
                <Link to={`/jobs/${j.slug}`} className="block">
                  <h2 className="font-bold text-slate-900 hover:text-orange-700 leading-snug">{j.title}</h2>
                  <p className="text-xs text-slate-600 mt-1">
                    {j.organization || ""}
                    {j.department ? ` · ${j.department}` : ""}
                    {j.total_posts ? ` · ${j.total_posts} posts` : ""}
                    {j.last_date ? ` · Last date: ${fmtDate(j.last_date)}` : ""}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Other states */}
      <section className="mt-10 bg-slate-50 border border-slate-200 rounded-xl p-5 md:p-6">
        <h2 className="text-lg font-extrabold text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          Browse other states
        </h2>
        <ul className="flex flex-wrap gap-2 text-sm">
          {Object.entries(STATES).map(([slug, info]) => (
            <li key={slug}>
              <Link
                to={`/states/${slug}`}
                className={`inline-flex items-center min-h-11 px-3 py-2 rounded-full border ${
                  slug === stateSlug
                    ? "bg-orange-100 border-orange-300 text-orange-800 font-bold"
                    : "bg-white border-slate-200 hover:border-orange-300 text-slate-700"
                }`}
              >
                {info.state}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
